<script lang="ts">
  import { _, locale } from 'svelte-i18n';
  import { createEventDispatcher } from 'svelte';
  import type { Document } from '../api';
  import { navigate } from 'svelte-routing';
  import { userSettings } from '../stores/settingsStore';
  import { toastStore } from '../stores/toastStore';
  import { formatDate, ALL_LIST_COLUMN_KEYS, LIST_COLUMN_LABEL_KEYS } from '../settings';
  import type { ListColumnKey } from '../settings';
  import {
    getLocalizedClassificationCategoryName,
    getLocalizedClassificationSubCategoryName,
    getLocalizedCombinedCategoryLabel,
  } from '../categoriesLocalization';
  import { categoriesStore } from '../stores/categoriesStore';
  import { memberNames, formatAssignees, resolveMemberName } from '../stores/memberNamesStore';
  import { isProcessableType } from '../fileSupport';
  import type { Category } from '../api';
  import { resolveSubject } from './treeGridColumns';
  import { hasAiGuessWarning } from '../warningFlags';
  import { FileText, AlertTriangle, Columns } from 'lucide-svelte';
  import { Spinner, TableSortHeader, FilterToolbar, PaginationBar } from './ui/index';
  import type { FilterDef, FilterValue } from './ui/FilterToolbar.svelte';

  interface DocumentFilterState {
    search?: string | null;
    fileFormat?: string | null;
    fromDate?: string | null;
    toDate?: string | null;
    minSize?: number | null;
    maxSize?: number | null;
    category?: string | null;
    subCategory?: string | null;
    assignedTo?: string | null;
    userTag?: string | null;
  }

  export let documents: Document[];
  export let loading = false;
  export let hasMore = false;
  export let hasPrev = false;
  export let totalCount: number | null = null;
  export let page = 0;
  export let pageSize = 20;
  export let sortBy: string | null = null;
  export let sortOrder: 'asc' | 'desc' | null = null;
  export let filterState: DocumentFilterState = {};
  /** Show the built-in filter bar. Browse renders one bar above all presentations (Spec 120 E3). */
  export let showFilters = true;
  /**
   * Controlled multiselect state (Spec 009 E3).
   * Parent owns the set; DocumentTable reflects it in row checkboxes and dispatches
   * selectionChange events when the user toggles rows or uses select-all.
   */
  export let selectedIds: Set<string> = new Set();

  const dispatch = createEventDispatcher<{
    sort: { key: string; order: 'asc' | 'desc' | null };
    filter: { key: string; value: FilterValue };
    clearAll: void;
    pageChange: number;
    pageSizeChange: number;
    /** Emitted whenever the row selection changes (Spec 009 E3). */
    selectionChange: { selectedIds: string[] };
  }>();

  // Spec 065 E6/S12b, widened by Spec 084 E6/S9 (review F2: shared with Inbox.svelte via
  // lib/warningFlags.ts): the existing low-confidence triangle also fires when the AI response
  // was unparsable or classification fell back to a guess — just as much a guess in both cases,
  // even when the confidence value itself looks unremarkable.
  function hasAiWarning(doc: Document): boolean {
    return hasAiGuessWarning(doc.extractedData?.extractedValues?.['system:warningFlags']);
  }

  function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  function getDocumentUrl(doc: Document): string {
    const identifier = doc.slug || doc.id;
    return `/documents/${identifier}`;
  }

  function handleRowClick(e: MouseEvent, doc: Document) {
    if (e.ctrlKey || e.metaKey || e.button === 1) return;
    e.preventDefault();
    navigate(getDocumentUrl(doc));
  }

  function handleSort(e: CustomEvent<{ key: string; order: 'asc' | 'desc' | null }>) {
    dispatch('sort', e.detail);
  }

  // Build enum options for format filter
  const formatOptions = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];
  const formatLabels = ['PDF', 'JPEG', 'PNG', 'Word (DOC)', 'Word (DOCX)'];

  // Categories for enum filters
  $: allCategories = $categoriesStore as Category[];
  $: categoryOptions = allCategories.map((c: Category) => c.name);
  $: categoryLabels = allCategories.map((c: Category) => c.displayName);
  $: selectedCatData = filterState.category
    ? (allCategories.find((c: Category) => c.name === filterState.category) ?? null)
    : null;
  $: subCategoryOptions = selectedCatData ? selectedCatData.subCategories.map((s) => s.name) : [];
  $: subCategoryLabels = selectedCatData
    ? selectedCatData.subCategories.map((s) => s.displayName)
    : [];

  // Spec 019: which flat-list columns the user has chosen to show. Absent/empty
  // listColumns means "all visible" (E3, bestandsverhalten inkl. Betreff-Spalte aus iter-15).
  // "name" is always visible regardless of the stored preference (E2).
  $: effectiveListColumns =
    $userSettings.listColumns && $userSettings.listColumns.length > 0
      ? $userSettings.listColumns
      : [...ALL_LIST_COLUMN_KEYS];
  $: visibleColumnSet = new Set(effectiveListColumns);
  // `visibleColumnSet` is passed explicitly (not read from closure) so Svelte's template
  // dependency tracking re-evaluates every {#if isColumnVisible(...)} block when it changes.
  function isColumnVisible(key: ListColumnKey, visible: Set<string>): boolean {
    return key === 'name' || visible.has(key);
  }

  let columnsMenuOpen = false;

  function toggleColumnsMenu() {
    columnsMenuOpen = !columnsMenuOpen;
  }

  function closeColumnsMenu() {
    columnsMenuOpen = false;
  }

  function handleColumnsClickOutside(e: MouseEvent) {
    const target = e.target as HTMLElement;
    if (!target.closest('.columns-toggle-dropdown')) {
      closeColumnsMenu();
    }
  }

  function handleColumnsKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && columnsMenuOpen) {
      closeColumnsMenu();
    }
  }

  async function toggleColumn(key: ListColumnKey) {
    if (key === 'name') return; // always visible, not toggleable (E2)
    const next = new Set(effectiveListColumns);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    try {
      await userSettings.updateSettings({ listColumns: [...next] });
    } catch (err) {
      console.error('Failed to update list columns:', err);
      toastStore.error($_('components.document_table.columns_update_error'));
    }
    closeColumnsMenu();
  }

  // Collect unique assignedTo values from the current document set for the filter dropdown.
  // When documents are loaded we already have all assignees visible in this page, which is
  // sufficient for a practical enum filter (full member list would require a separate API call).
  $: assignedToOptionsRaw = (() => {
    const seen = new Set<string>();
    for (const doc of documents) {
      for (const m of doc.assignedTo ?? []) {
        seen.add(m);
      }
    }
    // Also include the currently active filter value so the chip stays valid even when
    // the current page no longer contains that assignee.
    if (filterState.assignedTo) seen.add(filterState.assignedTo);
    return [...seen].sort();
  })();
  // Filter dropdown shows display names; the option values stay member ids (sent to API).
  $: assignedToOptionLabels = assignedToOptionsRaw.map((id) => resolveMemberName(id, $memberNames));

  // Available filters for toolbar (excluding parent-managed filters)
  $: availableFilters = [
    { key: 'search', label: $_('table.filter.search'), type: 'text' as const },
    {
      key: 'fileFormat',
      label: $_('components.document_table.column_format'),
      type: 'enum' as const,
      options: formatOptions,
      optionLabels: formatLabels,
    },
    {
      key: 'fileSize',
      label: $_('components.document_table.column_size'),
      type: 'number-range' as const,
    },
    {
      key: 'dateRange',
      label: $_('components.document_table.column_date_reference'),
      type: 'date-range' as const,
    },
    {
      key: 'category',
      label: $_('components.document_table.column_category'),
      type: 'enum' as const,
      options: categoryOptions,
      optionLabels: categoryLabels,
    },
    {
      key: 'subCategory',
      label: $_('components.document_table.column_subcategory'),
      type: 'enum' as const,
      options: subCategoryOptions,
      optionLabels: subCategoryLabels,
    },
    {
      key: 'assignedTo',
      label: $_('components.document_table.column_assigned_to'),
      type: 'enum' as const,
      options: assignedToOptionsRaw,
      optionLabels: assignedToOptionLabels,
    },
  ] satisfies FilterDef[];

  // Map filterState to toolbar-compatible activeFilters
  $: toolbarFilters = buildToolbarFilters(filterState);

  function buildToolbarFilters(state: DocumentFilterState): Record<string, FilterValue> {
    const result: Record<string, FilterValue> = {};
    if (state.search) result.search = state.search;
    if (state.fileFormat) result.fileFormat = [state.fileFormat];
    if (state.fromDate || state.toDate)
      result.dateRange = { from: state.fromDate, to: state.toDate };
    if (state.minSize != null || state.maxSize != null)
      result.fileSize = { min: state.minSize, max: state.maxSize };
    if (state.category) result.category = [state.category];
    if (state.subCategory) result.subCategory = [state.subCategory];
    if (state.assignedTo) result.assignedTo = [state.assignedTo];
    if (state.userTag) result.userTag = [state.userTag];
    return result;
  }

  function handleFilterChange(e: CustomEvent<{ key: string; value: FilterValue }>) {
    dispatch('filter', e.detail);
  }

  function handleFilterRemove(e: CustomEvent<{ key: string }>) {
    dispatch('filter', { key: e.detail.key, value: null });
  }

  function handleClearAll() {
    dispatch('clearAll');
  }

  // --- Multiselect (Spec 009 E3) ---

  /**
   * Toggles a single row's selection state and dispatches the updated set.
   * The `checked` value comes from the native checkbox after the browser has
   * already toggled it (called from on:change).
   */
  function handleRowToggle(id: string, e: Event) {
    const checked = (e.target as HTMLInputElement).checked;
    const next = new Set(selectedIds);
    if (checked) {
      next.add(id);
    } else {
      next.delete(id);
    }
    dispatch('selectionChange', { selectedIds: [...next] });
  }

  /**
   * Select / deselect all VISIBLE documents on the current page. Additive: toggles only the
   * current page's rows and preserves selections made on other pages (Spec 009 E3).
   */
  function handleSelectAll() {
    const next = new Set(selectedIds);
    if (allOnPageSelected) {
      documents.forEach((d) => next.delete(d.id)); // deselect current page only
    } else {
      documents.forEach((d) => next.add(d.id)); // select all visible on this page
    }
    dispatch('selectionChange', { selectedIds: [...next] });
  }

  $: allOnPageSelected = documents.length > 0 && documents.every((d) => selectedIds.has(d.id));

  /**
   * Returns a single display label for the document's classification.
   * Delegates to getLocalizedCombinedCategoryLabel which handles the catch-all
   * pair (miscellaneous/other → single locale label) and normal categories
   * ("Category › SubCategory").
   */
  function getCombinedCategoryLabel(doc: Document): string {
    if (!doc.classification) return '—';
    return getLocalizedCombinedCategoryLabel(
      doc.classification.category,
      doc.classification.subCategory ?? null,
      $locale,
    );
  }
</script>

<svelte:window on:click={handleColumnsClickOutside} on:keydown={handleColumnsKeydown} />

<div class="bg-surface border border-border rounded-lg overflow-hidden">
  <div class="flex flex-wrap items-center justify-between">
    <div class="flex-1 min-w-0">
      {#if showFilters}
        <FilterToolbar
          {availableFilters}
          activeFilters={toolbarFilters}
          on:filterChange={handleFilterChange}
          on:filterRemove={handleFilterRemove}
          on:clearAll={handleClearAll}
        />
      {:else}
        <div class="py-2"></div>
      {/if}
    </div>

    <!-- "Spalten"-Dropdown (Spec 019 E4): ein-/ausblendbare Flat-List-Spalten -->
    <div class="relative columns-toggle-dropdown shrink-0 pr-4">
      <button
        type="button"
        data-testid="columns-button"
        class="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-border bg-transparent text-text-muted hover:text-text hover:bg-surface-hover transition-colors cursor-pointer"
        on:click|stopPropagation={toggleColumnsMenu}
      >
        <Columns class="w-3.5 h-3.5" strokeWidth={1.5} />
        {$_('components.document_table.columns_button')}
      </button>

      {#if columnsMenuOpen}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div
          data-testid="columns-menu"
          data-esc-owner
          class="absolute right-0 top-full mt-1 z-50 bg-surface border border-border rounded-lg shadow-lg min-w-[220px] py-1"
          on:click|stopPropagation
        >
          <div class="px-3 py-1.5 text-xs font-semibold text-text-muted uppercase">
            {$_('components.document_table.columns_menu_title')}
          </div>
          {#each ALL_LIST_COLUMN_KEYS as key (key)}
            <label
              class="flex items-center gap-2 px-3 py-1.5 text-sm text-text cursor-pointer hover:bg-surface-hover transition-colors"
            >
              <input
                type="checkbox"
                data-testid={`column-toggle-${key}`}
                checked={isColumnVisible(key, visibleColumnSet)}
                disabled={key === 'name'}
                on:change={() => toggleColumn(key)}
                class="accent-accent cursor-pointer disabled:cursor-not-allowed"
              />
              {$_(LIST_COLUMN_LABEL_KEYS[key])}
            </label>
          {/each}
        </div>
      {/if}
    </div>
  </div>

  {#if loading}
    <div class="flex justify-center py-12">
      <Spinner size="lg" label={$_('messages.loading')} />
    </div>
  {:else if documents.length === 0}
    <div class="flex flex-col items-center justify-center py-12 px-4 text-center">
      <FileText class="w-8 h-8 text-text-muted mb-2" strokeWidth={1.5} />
      <p class="text-sm text-text-secondary">{$_('components.document_table.empty_state')}</p>
    </div>
  {:else}
    <div class="overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="border-b border-border">
            <!-- Multiselect column (Spec 009 E3): select-all button (not a checkbox to keep
                 screen.queryAllByRole('checkbox') results as pure row checkboxes) -->
            <th class="px-2 py-3 w-8 text-center">
              <button
                type="button"
                on:click={handleSelectAll}
                class="text-xs text-text-muted hover:text-text transition-colors"
                title={allOnPageSelected
                  ? $_('table.deselect_all')
                  : $_('table.select_all_on_page')}
                aria-label={allOnPageSelected
                  ? $_('table.deselect_all')
                  : $_('table.select_all_on_page')}
              >
                {allOnPageSelected ? '−' : '☐'}
              </button>
            </th>
            <TableSortHeader
              label={$_('components.document_table.column_name')}
              sortKey="fileName"
              currentSortBy={sortBy}
              currentSortOrder={sortOrder}
              on:sort={handleSort}
            />
            <!-- Subject column: user-configurable visibility (Spec 019 E5) -->
            {#if isColumnVisible('subject', visibleColumnSet)}
              <th
                class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-muted hidden md:table-cell"
              >
                {$_('components.document_table.column_subject')}
              </th>
            {/if}
            {#if isColumnVisible('format', visibleColumnSet)}
              <th
                class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-muted hidden md:table-cell"
              >
                {$_('components.document_table.column_format')}
              </th>
            {/if}
            {#if isColumnVisible('size', visibleColumnSet)}
              <TableSortHeader
                label={$_('components.document_table.column_size')}
                sortKey="fileSize"
                currentSortBy={sortBy}
                currentSortOrder={sortOrder}
                thClass="hidden md:table-cell"
                on:sort={handleSort}
              />
            {/if}
            <!-- Reference date (primary): shown on sm+ screens; full text-text weight -->
            {#if isColumnVisible('referenceDate', visibleColumnSet)}
              <TableSortHeader
                label={$_('components.document_table.column_date_reference')}
                sortKey="referenceDate"
                currentSortBy={sortBy}
                currentSortOrder={sortOrder}
                thClass="hidden sm:table-cell"
                on:sort={handleSort}
              />
            {/if}
            <!-- Upload date (secondary): hidden on small screens; muted header -->
            {#if isColumnVisible('uploadedAt', visibleColumnSet)}
              <TableSortHeader
                label={$_('components.document_table.column_date_uploaded')}
                sortKey="uploadedAt"
                currentSortBy={sortBy}
                currentSortOrder={sortOrder}
                thClass="hidden lg:table-cell"
                on:sort={handleSort}
              />
            {/if}
            <!-- Combined category › subcategory column (replaces two separate columns) -->
            {#if isColumnVisible('category', visibleColumnSet)}
              <th
                class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-muted hidden lg:table-cell"
              >
                {$_('components.document_table.column_category_combined')}
              </th>
            {/if}
            <!-- Assigned to column: visible on xl+ screens, sortable (primary assignee) + filterable via toolbar -->
            {#if isColumnVisible('assignedTo', visibleColumnSet)}
              <TableSortHeader
                label={$_('components.document_table.column_assigned_to')}
                sortKey="assignedTo"
                currentSortBy={sortBy}
                currentSortOrder={sortOrder}
                thClass="hidden xl:table-cell"
                on:sort={handleSort}
              />
            {/if}
          </tr>
        </thead>
        <tbody>
          {#each documents as doc (doc.id)}
            <tr
              on:click={(e) => handleRowClick(e, doc)}
              class="border-b border-border/50 cursor-pointer transition-colors hover:bg-surface-hover"
            >
              <!-- Row checkbox (Spec 009 E3). Stop propagation on the whole cell so a click
                   anywhere in the checkbox column toggles the box and never opens the row. -->
              <td class="px-2 py-3 w-8 text-center" on:click|stopPropagation>
                <label class="flex items-center justify-center cursor-pointer p-2 -m-2">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(doc.id)}
                    on:change={(e) => handleRowToggle(doc.id, e)}
                    class="accent-accent cursor-pointer"
                    aria-label={doc.displayName || doc.fileName}
                  />
                </label>
              </td>
              <td class="px-4 py-3 whitespace-nowrap max-w-[300px]">
                <a
                  href={getDocumentUrl(doc)}
                  class="flex items-center gap-2 text-text no-underline"
                  on:click|preventDefault
                >
                  <div class="relative">
                    <FileText class="w-4 h-4 text-text-muted shrink-0" strokeWidth={1.5} />
                    {#if (doc.classification && doc.classification.confidence < 0.7 && !doc.classification.isManualClassified) || hasAiWarning(doc)}
                      <div
                        class="absolute -top-1.5 -right-1.5 bg-accent rounded-full p-0.5 border border-surface"
                        data-testid="doc-row-warning-badge"
                      >
                        <AlertTriangle class="w-2.5 h-2.5 text-on-accent" strokeWidth={3} />
                      </div>
                    {/if}
                  </div>
                  <span class="text-sm text-text-muted truncate"
                    >{doc.displayName || doc.fileName}</span
                  >
                  {#if !isProcessableType(doc.mimeType)}
                    <span
                      class="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-bg-alt text-text-muted border border-border"
                      title={$_('components.document_table.unprocessed_hint')}
                    >
                      {$_('components.document_table.unprocessed_badge')}
                    </span>
                  {/if}
                </a>
              </td>
              <!-- Subject cell: user-configurable visibility (Spec 019 E5) -->
              {#if isColumnVisible('subject', visibleColumnSet)}
                {@const subjectText = resolveSubject(doc)}
                <!-- Flexible column: w-full + max-w-0 lets it absorb free space and grow with the
                     viewport; the inner truncate ellipsis-trims to the actual cell width. -->
                <td class="px-4 py-3 hidden md:table-cell w-full max-w-0">
                  <span class="text-sm font-medium text-text truncate block" title={subjectText}>
                    {subjectText}
                  </span>
                </td>
              {/if}
              {#if isColumnVisible('format', visibleColumnSet)}
                <td class="px-4 py-3 whitespace-nowrap hidden md:table-cell">
                  <span class="text-xs font-mono text-text-muted uppercase"
                    >{doc.mimeType?.split('/')[1]?.toUpperCase() ?? '—'}</span
                  >
                </td>
              {/if}
              {#if isColumnVisible('size', visibleColumnSet)}
                <td class="px-4 py-3 whitespace-nowrap hidden md:table-cell">
                  <span class="text-sm text-text-muted">{formatFileSize(doc.fileSize)}</span>
                </td>
              {/if}
              <!-- Reference date: primary date, full text-text weight -->
              {#if isColumnVisible('referenceDate', visibleColumnSet)}
                <td class="px-4 py-3 whitespace-nowrap hidden sm:table-cell">
                  <span class="text-sm text-text">
                    {doc.referenceDate ? formatDate(doc.referenceDate, $userSettings) : '—'}
                  </span>
                </td>
              {/if}
              <!-- Upload date: secondary, text-muted to indicate lower priority -->
              {#if isColumnVisible('uploadedAt', visibleColumnSet)}
                <td class="px-4 py-3 whitespace-nowrap hidden lg:table-cell">
                  <span class="text-sm text-text-muted"
                    >{formatDate(doc.uploadedAt, $userSettings)}</span
                  >
                </td>
              {/if}
              <!-- Combined category › subcategory -->
              {#if isColumnVisible('category', visibleColumnSet)}
                <td class="px-4 py-3 whitespace-nowrap hidden lg:table-cell max-w-[220px]">
                  {#if doc.classification}
                    <span
                      class="text-sm text-text truncate block"
                      title={getCombinedCategoryLabel(doc)}
                    >
                      {getCombinedCategoryLabel(doc)}
                    </span>
                  {:else}
                    <span class="text-sm text-text-muted">—</span>
                  {/if}
                </td>
              {/if}
              <!-- Assigned to: hidden on small screens, always filterable via toolbar -->
              {#if isColumnVisible('assignedTo', visibleColumnSet)}
                <td class="px-4 py-3 whitespace-nowrap hidden xl:table-cell">
                  {#if doc.assignedTo && doc.assignedTo.length > 0}
                    <span class="text-sm text-text"
                      >{formatAssignees(doc.assignedTo, $memberNames)}</span
                    >
                  {:else}
                    <span class="text-sm text-text-muted">—</span>
                  {/if}
                </td>
              {/if}
            </tr>
          {/each}
        </tbody>
      </table>
    </div>

    <PaginationBar
      {page}
      {pageSize}
      {totalCount}
      {hasMore}
      {hasPrev}
      on:pageChange={(e) => dispatch('pageChange', e.detail)}
      on:pageSizeChange={(e) => dispatch('pageSizeChange', e.detail)}
    />
  {/if}
</div>
